/**
 * RunHistoryDrawer — Sprint 4.6 (+ Sprint UX-BR-3 i18n).
 *
 * Drawer lateral com histórico de runs do template.
 */
import { useEffect, useState } from "react";
import { cancelConfigTemplateRun, getConfigTemplateRun, listConfigTemplateRuns } from "../../lib/api.js";
import { t } from "../../i18n/index.js";

const STATUS_COLORS = {
  completed: "text-noc-ok bg-noc-ok/10 border-noc-ok/30",
  partial:   "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
  failed:    "text-noc-err bg-noc-err/10 border-noc-err/30",
  dry_run:   "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
  // Sprint 4.6.7 — run cancelado manualmente pelo operador
  canceled:  "text-noc-purple bg-noc-purple/10 border-noc-purple/30",
};

// Sprint UX-BR-3 — Label traduzido por status. Mantém fallback uppercase()
// para statuses desconhecidos (forward-compat com backend).
function statusLabel(status) {
  const localized = t(`runHistory.status.${status}`);
  // Se a chave não existe, t() devolve a própria chave — detectamos isso
  // e usamos uppercase do status raw.
  if (localized.startsWith("runHistory.")) {
    return (status || "—").toUpperCase();
  }
  return localized.toUpperCase();
}

// Sprint 4.6.7 — runs canceláveis (têm commands reais que podem estar
// aguardando entrega)
const CANCELABLE_STATUSES = new Set(["completed", "partial"]);

function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s atrás`;
  if (s < 3600) return `${Math.floor(s / 60)}min atrás`;
  if (s < 86400) return `${Math.floor(s / 3600)}h atrás`;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function RunHistoryDrawer({ open, templateId, onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  // Sprint 4.6.7 — cancelamento
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  const [cancelErr, setCancelErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await listConfigTemplateRuns({ templateId, limit: 50 });
        setRuns(r.runs || []);
      } catch (e) {
        console.warn("runs failed:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, templateId]);

  const openDetail = async (runId) => {
    try {
      const detail = await getConfigTemplateRun(runId);
      setSelected(detail);
      setCancelResult(null);
      setCancelErr(null);
    } catch (e) {
      console.warn("run detail failed:", e);
    }
  };

  // Sprint 4.6.7 — cancelar run em andamento
  const cancelRun = async () => {
    if (!selected) return;
    const ok = window.confirm(t("runHistory.cancelConfirm"));
    if (!ok) return;
    setCancelBusy(true);
    setCancelErr(null);
    try {
      const r = await cancelConfigTemplateRun(selected.id);
      setCancelResult(r);
      // Recarrega lista + detail
      const refreshed = await listConfigTemplateRuns({ templateId, limit: 50 });
      setRuns(refreshed.runs || []);
      const updated = await getConfigTemplateRun(selected.id);
      setSelected(updated);
    } catch (e) {
      setCancelErr(e?.message || t("runHistory.errCancel"));
    } finally {
      setCancelBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[2050] bg-black/60 flex justify-end"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-noc-panel border-l border-noc-accent/30 shadow-2xl w-full max-w-xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <h3 className="text-sm font-semibold text-noc-accent">
            {templateId ? t("runHistory.titleTemplate") : t("runHistory.titleGeneral")}
          </h3>
          <button type="button" onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading && (
            <div className="text-xs text-noc-muted">{t("common.loading")}</div>
          )}
          {!loading && runs.length === 0 && (
            <div className="text-xs text-noc-muted italic">{t("runHistory.empty")}</div>
          )}
          {!loading && runs.map(r => (
            <div key={r.id}
                 onClick={() => openDetail(r.id)}
                 className="bg-noc-bg/40 border border-noc-border/60 rounded p-2 text-xs cursor-pointer hover:border-noc-accent/30">
              <div className="flex items-center justify-between gap-2">
                <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${STATUS_COLORS[r.status] || ""}`}>
                  {statusLabel(r.status)}
                </span>
                {r.dry_run && (
                  <span className="text-[10px] text-noc-accent">{t("runHistory.dry")}</span>
                )}
                <span className="text-[10px] text-noc-muted/70 ml-auto">
                  {fmtRel(r.created_at)}
                </span>
              </div>
              <div className="mt-1 text-[10px] text-noc-text/80 font-mono">
                {r.target_count} {t("runHistory.targets").toLowerCase()} · {r.created_commands} cmds
                {r.skipped_devices > 0 && ` · ${r.skipped_devices}${t("runHistory.skippedSuffix")}`}
              </div>
              <div className="mt-0.5 text-[10px] text-noc-muted/60">
                {r.created_by}
              </div>
            </div>
          ))}
        </div>

        {/* Detail modal */}
        {selected && (
          <div className="absolute inset-0 z-[2150] bg-black/80 flex items-center justify-center p-4"
               onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
            <div className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-2xl flex flex-col"
                 style={{ maxHeight: "90vh" }}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
                <h4 className="text-sm font-semibold text-noc-accent">
                  {t("runHistory.runId", { short: selected.id.substring(0, 8) })}
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLORS[selected.status] || ""}`}>
                    {statusLabel(selected.status)}
                  </span>
                  {selected.dry_run && (
                    <span className="ml-2 text-[10px] text-noc-accent">{t("runHistory.dryRun")}</span>
                  )}
                </h4>
                <div className="flex items-center gap-2">
                  {/* Sprint 4.6.7 — botão Cancelar Run */}
                  {!selected.dry_run && CANCELABLE_STATUSES.has(selected.status) && (
                    <button type="button" onClick={cancelRun} disabled={cancelBusy}
                      className="px-2 py-1 text-[10px] rounded border border-noc-err/30 text-noc-err hover:bg-noc-err/10 disabled:opacity-50">
                      {cancelBusy ? t("runHistory.cancelling") : t("runHistory.cancelRun")}
                    </button>
                  )}
                  <button type="button" onClick={() => setSelected(null)}
                    className="text-noc-muted hover:text-noc-text text-xl leading-none">×</button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 text-xs space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Stat label={t("runHistory.targets")} v={selected.target_count} />
                  <Stat label={t("runHistory.created")} v={selected.created_commands} ok />
                  <Stat label={t("runHistory.skipped")} v={selected.skipped_devices} warn={selected.skipped_devices > 0} />
                </div>
                <div className="text-[10px] text-noc-muted">
                  {new Date(selected.created_at).toLocaleString("pt-BR")} · {selected.created_by}
                </div>

                {/* Sprint 4.6.7 — resultado do cancel */}
                {cancelErr && (
                  <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
                    {cancelErr}
                  </div>
                )}
                {cancelResult && (
                  <div className="text-xs bg-noc-purple/10 border border-noc-purple/30 rounded p-2 space-y-1">
                    <div className="text-noc-purple uppercase tracking-wide text-[10px]">
                      {t("runHistory.cancelTitle")}
                    </div>
                    <div className="text-noc-purple">
                      • <strong>{cancelResult.canceled_commands}</strong> {t("runHistory.cancelCmds")}
                    </div>
                    <div className="text-noc-purple">
                      • <strong>{cancelResult.already_acked}</strong> {t("runHistory.cancelAlreadyAcked")}
                    </div>
                    {cancelResult.skipped_terminal > 0 && (
                      <div className="text-noc-purple">
                        • <strong>{cancelResult.skipped_terminal}</strong> {t("runHistory.cancelTerminal")}
                      </div>
                    )}
                  </div>
                )}
                {/* Mostra resumo do cancel se já estava cancelado antes */}
                {selected.status === "canceled" && !cancelResult && selected.summary_json?.cancel_result && (
                  <div className="text-xs bg-noc-purple/10 border border-noc-purple/30 rounded p-2 space-y-1">
                    <div className="text-noc-purple uppercase tracking-wide text-[10px]">
                      {t("runHistory.cancelAt", {
                        date: selected.summary_json.canceled_at
                          ? new Date(selected.summary_json.canceled_at).toLocaleString("pt-BR")
                          : "—",
                      })}
                    </div>
                    <div className="text-noc-purple">
                      • <strong>{selected.summary_json.cancel_result.canceled_commands}</strong> {t("common.cancel").toLowerCase()}
                      &nbsp;•&nbsp; <strong>{selected.summary_json.cancel_result.already_acked}</strong> {t("status.acked").toLowerCase()}
                      &nbsp;•&nbsp; <strong>{selected.summary_json.cancel_result.skipped_terminal}</strong> {t("runHistory.cancelTerminal").replace(/\([^)]+\)/g,'').trim()}
                    </div>
                  </div>
                )}

                {selected.summary_json?.risks?.length > 0 && (
                  <div className="bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
                    <div className="text-noc-warn text-[10px] uppercase">{t("runHistory.risks")}</div>
                    {selected.summary_json.risks.map((rs, i) => (
                      <div key={i} className="text-noc-warn text-[11px]">• {rs}</div>
                    ))}
                  </div>
                )}
                <pre className="text-[10px] bg-noc-bg/40 border border-noc-border/60 rounded p-2 overflow-x-auto max-h-64">
                  {JSON.stringify(selected.summary_json, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, v, ok, warn }) {
  return (
    <div className={`bg-noc-bg/40 border rounded p-2 ${
      warn ? "border-noc-warn/30" : ok ? "border-noc-ok/30" : "border-noc-border"
    }`}>
      <div className="text-[10px] text-noc-muted uppercase tracking-wide">{label}</div>
      <div className={`font-mono mt-0.5 ${warn ? "text-noc-warn" : ok ? "text-noc-ok" : "text-noc-text"}`}>
        {v}
      </div>
    </div>
  );
}
